//  Service pour gerer les titres

import { data } from "jquery";
import apiClient from "./api";
/**
 * Crée un nouveau titre
 * @param {Object} securityData - Données du titre à créer
 * @returns {Promise<Object>} - Titre créé
 */
export const createSecurity = async (securityData) => {
  try {
    // console.log("🔍 [SecurityService] Création d'un titre:", securityData);

    const response = await apiClient.post('/api/Security', securityData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    if (response.status === 201) {
      return {
        success: true,
        data: response.data,
        message: "Titre créé avec succès"
      };
    }
    
    throw new Error(response.data?.message || "Erreur lors de la création");
  } catch (error) {
    // console.error("❌ [SecurityService] Erreur détaillée:", {
    //   status: error.response?.status,
    //   data: error.response?.data,
    //   message: error.message
    // });

    // Structure d'erreur standardisée
    const apiError = {
      success: false,
      message: error.response?.data?.title || "Erreur lors de la création de la sécurité",
      errors: error.response?.data?.errors || {},
      status: error.response?.status,
      details: error.response?.data?.detail
    };
    
    throw apiError;
  }
};

/**
 * Met à jour un titre existant
 * @param {Object} securityData - Données du titre à mettre à jour (doit inclure .id)
 * @returns {Promise<Object>} - Titre mis à jour
 */
export const updateSecurity = async (securityData) => {
  try {
    if (!securityData.id) {
      throw new Error("L'ID du titre est requis pour la mise à jour");
    }

    // console.log("🔍 [SecurityService] Mise à jour du titre ID:", securityData.id);

    const response = await apiClient.put(`/api/Security/${securityData.id}`, securityData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
        message: "Titre mis à jour avec succès"
      };
    }
    
    throw new Error(response.data?.message || "Erreur lors de la mise à jour");
  } catch (error) {
    // console.error(" [SecurityService] Erreur lors de la mise à jour:", error);

    const apiError = {
      success: false,
      message: error.response?.data?.title || "Erreur lors de la mise à jour du titre",
      errors: error.response?.data?.errors || {},
      status: error.response?.status,
      details: error.response?.data?.detail
    };
    
    throw apiError;
  }
};

/**
 * Récupère un titre par son ID
 * @param {number} id - ID du titre
 * @returns {Promise<Object>} - Titre correspondant
 */
export const getSecurityById = async (id) => {
  try {
    const response = await apiClient.get(`/api/Security/${id}`);
    
    if (response.status === 200) {
      return {
        success: true,
        data: response.data,
        message: "Titre récupéré avec succès"
      };
    }
    
    throw new Error(response.data?.message || "Titre non trouvé");
  } catch (error) {
    
    const apiError = {
      success: false,
      message: error.response?.data?.title || "Erreur lors de la récupération du titre",
      status: error.response?.status
    };
    
    throw apiError;
  }
};

/**
 * Formater les données de titre pour l'API
 * @param {Object} formData - Données du formulaire
 * @param {string} mode - 'create' ou 'update'
 * @returns {Object} - Données formatées pour l'API
 */
export const formatSecurityDataForApi = (formData, mode = 'create') => {
  const formattedData = new FormData();
  
  // Champs communs
  const fields = [
    'issuerAccountId', 'symbol', 'isin', 'name', 'targetMarket', 'type',
    'totalUnitsIssue', 'issuePrice', 'lastPrice', 'couponFrequency',
    'couponRate', 'issueDate', 'maturity', 'dateJouissance'
  ];
  
  fields.forEach(field => {
    const value = formData[field];
    if (value !== undefined && value !== null && value !== '') {
      formattedData.append(field, value);
    }
  });
  
  // Champs booléens
  if (formData.repayable !== undefined && formData.repayable !== null) {
    formattedData.append('repayable', formData.repayable.toString());
  }
  
  // Pour la création seulement
  if (mode === 'create') {
    // S'assurer que lastPrice est défini (utilise issuePrice par défaut)
    if (!formData.lastPrice && formData.issuePrice) {
      formattedData.append('lastPrice', formData.issuePrice);
    }
  }
  
  return formattedData;
};

// 🔹 Récupérer les securities publiques (sans domainId)
export const getSecuritiesByDomain = async (params) => {
  try {
    const response = await apiClient.get('/api/Security/all/public', { params });
    return response.data; 
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return { items: [], message: error.response.data?.message || "Aucun titre trouvé" };
    }
    // console.error("Erreur lors de la récupération des securities :", error);
    // throw error("Erreur lors de la récupération des securities :", error);
  }
};

export const getSecuritiesByDomainMonetary = async (params) => {
  try {
    const response = await apiClient.get('/api/Security/all/monetary', { params });
    return response.data;
  } catch (error) {
    // Si 404, renvoie un tableau vide
    if (error.response && error.response.status === 404) {
      return { items: [], message: error.response.data?.Message || "Aucun titre trouvé" };
    }
    throw error; // pour les autres erreurs
  }
};


// 🔹 Récupérer les securities privées (sans domainId)
// 🔹 Récupérer les securities privées (sans domainId)
export const getSecuritiesByDomains = async (params) => {
  try {
    const response = await apiClient.get('/api/Security/all/PrivateSecuties', { params });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return { 
        items: [], 
        totalCount: 0,
        pageNumber: params.pageNumber || 1,
        pageSize: params.pageSize || 10,
        totalPages: 0,
        message: error.response.data?.message || "Aucun titre trouvé" 
      };
    }
    // console.error("Erreur lors du chargement des securities privées:", error);
    // throw error("Erreur lors du chargement des securities privées:", error);
  }
};

// 🔹 Récupérer les securities publiques (avec domainId dans l'URL)
// Dans votre service api/SecurityService.js
export const getPublicSecuritiesByDomain = async (domainId, params = {}) => {
  try {
    const response = await apiClient.get(`/api/Security/All/Security/Public/${domainId}`, { params });
    
    // Si l'API retourne un tableau simple, on simule la structure paginée
    if (Array.isArray(response.data)) {
      return {
        items: response.data,
        totalCount: response.data.length,
        pageNumber: params.pageNumber || 1,
        totalPages: Math.ceil(response.data.length / (params.pageSize || 4)),
        hasPreviousPage: (params.pageNumber || 1) > 1,
        hasNextPage: (params.pageNumber || 1) < Math.ceil(response.data.length / (params.pageSize || 4))
      };
    }
    
    return response.data; 
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return {
        items: [],
        totalCount: 0,
        pageNumber: 1,
        totalPages: 0,
        hasPreviousPage: false,
        hasNextPage: false
      };
    }
    // console.error("Erreur lors de la récupération des securities publiques :", error);
    throw error("Erreur lors de la récupération des securities publiques :", error);
  }
};

 /**
 * Service pour l'API avancée des titres avec tous les filtres
 */

export const getAdvancedSecurities = async (domainId, filters = {}, signal = null) => {
  try {
    const params = {
      DomainId: domainId,
      PageNumber: filters.pageNumber || 1,
      PageSize: filters.pageSize || 20,
      SecurityName: filters.securityName || '',
      TargetMarket: filters.targetMarket || '',
      SecurityType: filters.securityType || '',
      IssuerName: filters.issuerName || '',
      HolderType: filters.holderType || 'All',
      SortBy: filters.sortBy || 'SecurityName',
      SortDescending: filters.sortDescending || false,
      ...filters
    };

    // Nettoyer les paramètres vides
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null || params[key] === undefined) {
        delete params[key];
      }
    });

    const config = { params };
    if (signal) {
      config.signal = signal;
    }

    const response = await apiClient.get('/api/Security/advanced', config);
    return response.data;
  } catch (error) {
    if (error.name === 'CanceledError' || error.name === 'AbortError') {
      throw new Error('SearchCancelled');
    }
    
    if (error.response?.status === 404) {
      return {
        items: [],
        totalCount: 0,
        pageNumber: 1,
        pageSize: 20,
        totalPages: 0,
        hasPreviousPage: false,
        hasNextPage: false
      };
    }
    
    if (error.response?.status === 400) {
      const errorMessage = error.response.data?.message || 'Paramètres invalides';
      throw new Error(errorMessage);
    }
    
    // console.error("Erreur lors de la récupération des titres :", error);
    throw error("Erreur lors de la récupération des titres :", error);
  }
};

// 🔹 Récupérer les securities monétaires (avec domainId dans l'URL)
export const getMonetarySecuritiesByDomain = async (domainId, params = {}) => {
  try {
    const response = await apiClient.get(`/api/Security/All/Security/Monetary/${domainId}`, { params });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return []; // Retourne un tableau vide comme dans votre exemple
    }
    // console.error("Erreur lors de la récupération des securities monétaires :", error);
    throw error("Erreur lors de la récupération des securities monétaires :", error);
  }
};

// 🔹 Récupérer tous les titres avec pagination et filtre
export const getAllSecurities = async (params = {}) => {
  try {
    const response = await apiClient.get('/api/Security/domain', { params });
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return {
        items: [],
        totalCount: 0,
        pageNumber: params.pageNumber || 1,
        pageSize: params.pageSize || 10,
        totalPages: 0
      };
    }
    // console.error("Erreur lors de la récupération des titres :", error);
    throw error("Erreur lors de la récupération des titres :", error);
  }
};


// rechercger par targetmarket
export const getSecuritiesByMarket = async (targetMarket) => {
  try {
    const response = await apiClient.get(`/api/Security/market/${targetMarket}`);
    return response.data;
  } catch (error) {
    // console.error("Erreur lors de la récupération des securities par marché et domaine :", error);
    throw error("Erreur lors de la récupération des securities par marché et domaine :", error);
  }
};

// gerer et afficher l'historique des securities
export const getSecurityPriceHistory = async (securityId) => {
  try {
    const response = await apiClient.get(`/api/SecurityPrice/by-security/${securityId}`);
    return response.data;
  } catch (error) {
    // console.error("Erreur lors de la récupération de l'historique des prix :", error);
    throw error("Erreur lors de la récupération de l'historique des prix :", error);
  }
};

// supprimer par id une securities 
export const deleteSecurityById = async (id) => {
  try {
    await apiClient.delete(`/api/Security/DeleteSecurity/${id}`);
  } catch (error) {
    throw new Error("Erreur de suppression", error);
  }
};


// gerer et afficher les securities private (nomre total)
// Fonction pour récupérer le count et totalPrice des security privées
export const countPriveeSecurity = async () => {
  try {
    const response = await apiClient.get(`/api/Security/count-privee-security`);
    return response.data; 
  } catch (error) {
    // console.error('Error fetching private security count:', error);
    throw new Error('Impossible de récupérer les données de sécurité',error);
  }
};

// Fonction pour récupérer le count et totalPrice des security privées
export const countPublicSecurity = async () => {
  try {
    const response = await apiClient.get(`/api/Security/count-public-security`);
    return response.data; 
  } catch (error) {
    // console.error('Error fetching private security count:', error);
    throw new Error('Impossible de récupérer les données de sécurité',error);
  }
};

// Fonction pour récupérer le count et totalPrice des security privées
export const countMonetarySecurity = async () => {
  try {
    const response = await apiClient.get(`/api/Security/count-monetary-security`);
    return response.data; 
  } catch (error) {
    // console.error('Error fetching private security count:', error);
    throw new Error('Impossible de récupérer les données de sécurité');
  }
};
// Fonction pour formater totalPrice avec un 'K' si nécessaire
export const formatPrice = (price) => {
  if (price >= 1000) {
    return `${(price / 1000).toFixed(1)}K`; // Ajoute un 'K' pour les valeurs > 1000
  }
  return price.toFixed(2); // Assure un affichage avec 2 décimales
};

// gerer la modification du prix d'une securities
export const updateSecurityPrice = async (priceData) => {
  try {
    
    // Format exact attendu par le backend
    const payload = {
      Datetime: new Date().toISOString(), // Obligatoire: required DateTime
      SecurityId: parseInt(priceData.securityId, 10), // Obligatoire: required int
      Price: priceData.currentPrice ? parseFloat(priceData.currentPrice) : null, // decimal? (nullable)
      AdjustedPrice: priceData.adjustedPrice ? parseFloat(priceData.adjustedPrice) : null, // decimal? (nullable)
      Volume: priceData.volume ? parseInt(priceData.volume, 10) : null, // int? (nullable)
      LastUpdateDate: new Date().toISOString(), // DateTime? (nullable)
      IsDeleted: false // bool? (nullable)
    };
    
    
    const response = await apiClient.post(`/Api/SecurityPrice`, payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    return response.data;
    
  } catch (error) {
    // Message d'erreur plus spécifique
    if (error.response?.status === 500) {
      const errorMsg = error.response?.data?.Details || error.response?.data?.Message;
      throw new Error(`Erreur serveur: ${errorMsg}`);
    }
    
    throw error;
  }
};

// liste des securities d'un account 
export const getSecuritiesByIssuerAccountId = async (issuerAccountId, domainId, page=1 , pageSize=10) => {
  try {
    const response = await apiClient.get(
      `/api/Security/GetSecuritiesByIssuerAccountFirstName/IssuerAccountId/${issuerAccountId}/${domainId}?page=${page}&pageSize=${pageSize}`
    );
    // console.log("reponse securities client", data); 
    return response.data;
  } catch (error) {
    // console.error("Erreur lors de la récupération des securities par IssuerAccountId :", error);
    throw error("Erreur lors de la récupération des securities par IssuerAccountId :", error);
  }
};

// calculateTotalAmountOfBonds 
export const calculateTotalAmountOfBonds = async () => {
  try {
    const response = await apiClient.get(`/api/Security/bonds/total-amount`);
    return response.data;
  } catch (error) {
    // console.error('Erreur lors du calcul du montant total des bonds :', error);
    if (error.response) {
      // console.error('Code d\'état :', error.response.status);
      // console.error('Données de la réponse :', error.response.data);
    } else if (error.request) {
      // console.error('Aucune réponse reçue de l\'API.');
    } else {
      // console.error('Erreur de configuration de la requête :', error.message);
    }
    throw error;
  }
};

// calculateTotalAmountOfshares
export const calculateTotalAmountOfshares = async () => {
  try {
    const response = await apiClient.get(`/api/Security/shares/total-amount`);
    return response.data;
  } catch (error) {
    // console.error('Erreur lors du calcul du montant total des bonds :', error);
    if (error.response) {
      // console.error('Code d\'état :', error.response.status);
      // console.error('Données de la réponse :', error.response.data);
    } else if (error.request) {
      // console.error('Aucune réponse reçue de l\'API.');
    } else {
      // console.error('Erreur de configuration de la requête :', error.message);
    }
    throw error;
  }
};

// calculateTotalAmountOfEtf
export const calculateTotalAmountOfEtf = async () => {
  try {
    const response = await apiClient.get(`/api/Security/etf/total-amount`);
    return response.data;
  } catch (error) {
    // console.error('Erreur lors du calcul du montant total des bonds :', error);
    if (error.response) {
      // console.error('Code d\'état :', error.response.status);
      // console.error('Données de la réponse :', error.response.data);
    } else if (error.request) {
      // console.error('Aucune réponse reçue de l\'API.');
    } else {
      // console.error('Erreur de configuration de la requête :', error.message);
    }
    throw error;
  }
};

// calculateTotalAmountOfNote
export const calculateTotalAmountOfNote = async () => {
  try {
    const response = await apiClient.get(`/api/Security/Note/total-amount`);
    return response.data;
  } catch (error) {
    // console.error('Erreur lors du calcul du montant total des bonds :', error);
    if (error.response) {
      // console.error('Code d\'état :', error.response.status);
      // console.error('Données de la réponse :', error.response.data);
    } else if (error.request) {
      // console.error('Aucune réponse reçue de l\'API.');
    } else {
      // console.error('Erreur de configuration de la requête :', error.message);
    }
    throw error;
  }
};

// gerer le volume total des titres
export const getTotalUnits = async () => {
  try {
      const response = await apiClient.get('/api/Security/Security/count');
      return response.data;
  } catch (error) {
      // console.error('Erreur lors de la récupération du total des unités :', error);
      throw error('Erreur lors de la récupération du total des unités :', error);
  }
};

// cadh des titres totals
export const getTotalCash = async () => {
  try {
      const response = await apiClient.get('/api/Security/total-cash-all-security');
      return response.data;
  } catch (error) {
      // console.error('Erreur lors de la récupération du total des unités :', error);
      throw error('Erreur lors de la récupération du total des unités :', error);
  }
};

// Fonction pour obtenir le montant total du cash
export const getTotalCashSecurity = async (issueAccountId, domainId) => {
  try {
    const response = await apiClient.get(`/api/Security/GetAucClient/${issueAccountId}/${domainId}`);
    return response.data.totalAuc; // Retourne le montant total
  } catch (error) {
    // console.error("Erreur lors de la récupération du cash total", error);
    throw error("Erreur lors de la récupération du cash total", error);  // Propager l'erreur pour la gestion côté composant
  }
};

// Volume Auc par type de client (LYDIA)
export const getVolumeAucByClientType = async (domainId, year = null) => {
    const params = year ? { year } : {};
    const response = await apiClient.get(`/api/Security/VolumeAucByClientType/${domainId}`, { params });
    return response.data;
};

// 1. Récupérer le count et totalPrice des securities d'un client par son IssuerAccountId
export const countSecurityByIssuerAccountId = async (issuerAccountId, domainId) => {
  // console.log("issuerAccountId", issuerAccountId);
  // console.log("domainId", domainId);
  try {
    const response = await apiClient.get(
      `/api/Security/Security/count/IssuerAccountId/${issuerAccountId}/${domainId}`
    );
    return response.data; // attend { count, totalPrice }
  } catch (error) {
    // console.error("Erreur lors de la récupération du count des securities:", error);
    throw new error;
  }
};

// 2. Récupérer le total cash de tous les securities
export const getTotalCashOfAllSecurities = async () => {
  try {
    const response = await apiClient.get(`/api/Security/total-cash-all-security`);
    return response.data; // attend { totalCash }
  } catch (error) {
    // console.error("Erreur lors de la récupération du total cash de tous les securities:", error);
    throw new error;
  }
};

// recupoerer les comptes en fonction de la securities choisit 
export const getAccountBySecurity = async (securityId, domainId) => {
  try {
    const response = await apiClient.get(`/api/Security/AccountBySecurity/${securityId}/${domainId}`);
    return response.data;
  } catch {
    // console.error('Erreur lors de la récupération des comptes par sécurité :', error);
    // throw new error;
  }
};
// Récupérer les détenteurs d'un titre
// SecurityService.js - Modifiez la fonction getSecurityHolders
export const getSecurityHolders = async (domainId, securityId, pageNumber = 1, pageSize = 5, searchParams = {}) => {
  try {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      securityId: securityId.toString(),
      ...searchParams
    });

    const response = await apiClient.get(
      `/api/Security/getSecuritiesWithHolders/${domainId}?${params}`,
      {
        headers: {
          'accept': 'text/plain',
          'Content-Type': 'application/json',
        },
        responseType: 'json'
      }
    );
    
    return response.data;
  } catch (error) {
    // console.error('Erreur lors de la récupération des détenteurs:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des détenteurs');
  }
};
// Générer le rapport PDF des détenteurs
export const generateHoldersReport = async (domainId, securityId) => {
  try {
    const response = await apiClient.post(
      `/api/Security/generate-holders-report/${domainId}?securityId=${securityId}`,
      {}, // Body vide
      {
        headers: {
          'accept': '*/*',
        },
        responseType: 'blob' // Important pour les fichiers binaires
      }
    );
    
    // Récupérer le nom du fichier depuis les headers
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'HoldersReport.pdf';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    // Créer un URL pour le blob et déclencher le téléchargement
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    
    document.body.appendChild(a);
    a.click();
    
    // Nettoyer
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    
    return { success: true, filename };
  } catch (error) {
    // console.error('Erreur lors de la génération du rapport:', error);
    throw new Error(error.response?.data?.message || 'Erreur lors de la génération du rapport');
  }
};

// Récupérer le résumé des détenteurs d'un titre
export const getSecurityHoldersSummary = async (domainId, securityId = null, securityName = null, accountName = null, accountCompanyName = null) => {
  try {
    const params = new URLSearchParams();
    params.append('domainId', domainId);
    
    if (securityId) params.append('securityId', securityId);
    if (securityName) params.append('securityName', securityName);
    if (accountName) params.append('accountName', accountName);
    if (accountCompanyName) params.append('accountCompanyName', accountCompanyName);

    const response = await apiClient.get(`/api/Security/getSecurityHoldersSummary/${domainId}?${params.toString()}`, {
      headers: {
        'accept': 'text/plain'
      }
    });
    
    return response.data;
  } catch (error) {
    // console.error('Erreur lors de la récupération du résumé des détenteurs:', error);
    throw new error;
  }
};


// récupérer les statistiques des transactions privées
export const getPrivateTransactionsStatistics = async (params) => {
  try {
    const { domainId, startDate, endDate, transactionType, securityId } = params;
    
    const queryParams = new URLSearchParams();
    queryParams.append('domainId', domainId);
    
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);
    if (transactionType) queryParams.append('transactionType', transactionType);
    if (securityId) queryParams.append('securityId', securityId);
    
    const response = await apiClient.get(
      `/api/Dashboard/private-transactions/statistics?${queryParams.toString()}`
    );
    return response.data;
  } catch (error) {
    // console.error("Erreur lors de la récupération des statistiques des transactions privées:", error);
    throw new error;
  }
};